/**
 * Adapter: pv-layout-core callback output → Alurack API payloads.
 *
 * Converts data emitted by SolarPlanner callbacks (onSegmentCreate,
 * onSegmentUpdate, onPanelToggle) into the shapes expected by Alurack's
 * REST API and Redux actions.
 *
 * Alurack has no "v5/permissions" concept, so these helpers are named after
 * the projects they bridge rather than the library internal version.
 *
 * Key differences from the Sunwise adapter:
 *  - Extra Alurack custom_inputs fields (sloof_type, gclamp_type, system_uid,
 *    category, preset_configuration, type_application, and the solution_fields
 *    rail_spacing, rail_support_length, plate_support_length, beam_spacing,
 *    support_length, lfoot_supports_width, outer_rail_length,
 *    front_base_height, seam_distance, sheet_gauge) are written back by
 *    `_mergeCustomInputs` using the values the widget returns via
 *    `roofEditExtra` (they travel through `settings`).
 *  - No `panel_company_branch_office` (Sunwise specific).
 */
import { setCeilZone } from 'common/components/segment/asceHelpers';
import {
    POLYGON_CIRCLE,
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
} from 'common/components/segment/constants';
import {
    getModulesGroupV2,
    markGroupEdges,
    prepareModulesGroups,
} from 'common/components/segment/helpers';

import { detectFieldChanges } from '../helpers';

const TYPE_MAP = {
    roof: POLYGON_SEGMENT,
    obstacle: POLYGON_OBSTACLE,
    image: POLYGON_MAP,
};

const ALURACK_SOLUTION_FIELD_KEYS = [
    'beam_spacing',
    'front_base_height',
    'lfoot_supports_width',
    'outer_rail_length',
    'plate_support_length',
    'rail_spacing',
    'rail_support_length',
    'seam_distance',
    'sheet_gauge',
    'support_length',
];

const ALURACK_ROOT_CUSTOM_INPUT_KEYS = [
    'building_heading',
    'category',
    'gclamp_type',
    'land_category',
    'preset_configuration',
    'sloof_type',
    'system',
    'system_uid',
    'type_application',
    // Panel-tab metadata used by AlurackPanelTabExtra. Persisted as
    // custom_inputs so they survive save/load round-trips.
    'brandName',
    'is_custom_module',
    'panel_name',
    'panel_weight',
];

function _resolveAlurackType(segment) {
    return TYPE_MAP[segment.type] ?? POLYGON_SEGMENT;
}

/**
 * Rectangle roofs are persisted as POLYGON_SEGMENT at the backend, with
 * the true type captured under custom_inputs.drawing_type.
 */
function _resolveDrawingType(segment) {
    if (segment.type === 'roof' && segment.isRectangle) {
        return POLYGON_RECTANGLE;
    }
    return TYPE_MAP[segment.type] ?? POLYGON_SEGMENT;
}

function _ringToPolygon(ring) {
    if (!Array.isArray(ring)) return [];
    return ring.map((p) => [p.lat, p.lng]);
}

function _ringToFieldSegmentPoints(ring) {
    return ring.map((p) => ({
        x_coordinate: p.lng,
        y_coordinate: p.lat,
    }));
}

function _computeLayoutColIndices(panelsLayout) {
    const Y_THRESHOLD = 0.001; // ~110 m, generous for floating point
    const rows = [];

    for (const g of panelsLayout) {
        const lat = g.center?.lat ?? 0;
        const lng = g.center?.lng ?? 0;
        const found = rows.find((r) => Math.abs(r.lat - lat) < Y_THRESHOLD);
        if (found) {
            found.groups.push({ groupIndex: g.groupIndex, lng });
        } else {
            rows.push({
                lat,
                groups: [{ groupIndex: g.groupIndex, lng }],
            });
        }
    }

    for (const row of rows) {
        row.groups.sort((a, b) => a.lng - b.lng);
    }

    const colMap = {};
    for (const row of rows) {
        row.groups.forEach(({ groupIndex }, colIdx) => {
            colMap[groupIndex] = colIdx;
        });
    }
    return colMap;
}

function _buildSolarModulesFromLayout(panelsLayout) {
    if (!Array.isArray(panelsLayout) || panelsLayout.length === 0) return [];

    const modules = [];
    for (const group of panelsLayout) {
        for (const panel of group.panels) {
            modules.push({
                cell: 0,
                col: panel.col,
                group: group.groupIndex,
                is_enable: panel.isEnabled,
                row: panel.row,
                solar_module_points: JSON.stringify(
                    panel.corners.map((c) => ({ x: c.lng, y: c.lat })),
                ),
            });
        }
    }
    return modules;
}

export function buildGroupedCellsFromLayout(panelsLayout) {
    if (!Array.isArray(panelsLayout) || panelsLayout.length === 0) return [];

    const colMap = _computeLayoutColIndices(panelsLayout);

    return panelsLayout.map((group) => {
        const gridMap = new Map();
        const maxCols = group.cols ?? 1;
        const maxRows = group.rows ?? 1;

        for (const panel of group.panels) {
            const row = panel.row ?? 0;
            const col = panel.col ?? 0;
            gridMap.set(`${row}:${col}`, panel);
        }

        const groupRows = [];
        for (let r = 0; r < maxRows; r++) {
            const rowPanels = [];
            for (let c = 0; c < maxCols; c++) {
                const key = `${r}:${c}`;
                const panel = gridMap.get(key);
                const colInRow = colMap[group.groupIndex] ?? group.groupIndex;

                rowPanels.push({
                    cell: 0,
                    col: c,
                    enable: panel ? panel.isEnabled : false,
                    group: group.groupIndex,
                    internalId: `${group.groupIndex}-${r}-${c}-${colInRow}`,
                    paths: panel
                        ? panel.corners.map((corner) => [
                              corner.lat,
                              corner.lng,
                          ])
                        : [],
                    row: r,
                });
            }
            if (rowPanels.length > 0) {
                groupRows.push(rowPanels);
            }
        }
        return groupRows;
    });
}

function _toFieldSegmentPoint(point) {
    if (!point) return null;

    if (
        Number.isFinite(point.x_coordinate) &&
        Number.isFinite(point.y_coordinate)
    ) {
        return {
            x_coordinate: point.x_coordinate,
            y_coordinate: point.y_coordinate,
        };
    }

    if (Number.isFinite(point.x) && Number.isFinite(point.y)) {
        return {
            x_coordinate: point.x,
            y_coordinate: point.y,
        };
    }

    if (Number.isFinite(point.lat) && Number.isFinite(point.lng)) {
        return {
            x_coordinate: point.lng,
            y_coordinate: point.lat,
        };
    }

    if (Array.isArray(point) && point.length >= 2) {
        const lat = Number(point[0]);
        const lng = Number(point[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return {
                x_coordinate: lng,
                y_coordinate: lat,
            };
        }
    }

    return null;
}

/**
 * Build the solar_modules array for field_segments.
 *
 * Primary source: segment.panels_layout (from the 3D library). The library
 * emits this on every onSegmentCreate/onSegmentUpdate that includes a
 * recomputed panel layout.
 *
 * Fallback: originalSolarModules (the last API-saved solar_modules, stored in
 * Redux by formatSegment). This covers the case where onSegmentUpdate fires
 * for a settings-only change (e.g. after handlePreDrawFormSubmit calls
 * updateRoofSetting) and the library hasn't recomputed the layout yet —
 * in that scenario panels_layout is empty, but we must NOT send an empty
 * solar_modules array to the backend or we would wipe all panel_modules
 * records, breaking solar_modules / modules_groups in the report template.
 */
function _resolveSolarModules(segment, originalSolarModules) {
    const panelsLayout = Array.isArray(segment.panels_layout)
        ? segment.panels_layout
        : [];

    if (panelsLayout.length > 0) {
        return _buildSolarModulesFromLayout(panelsLayout);
    }

    // No layout from library — preserve previously saved data.
    if (
        Array.isArray(originalSolarModules) &&
        originalSolarModules.length > 0
    ) {
        return originalSolarModules.map((m) => ({
            cell: m.cell ?? 0,
            col: m.col,
            group: m.group,
            is_enable: m.enabled ?? m.is_enable ?? true,
            row: m.row,
            solar_module_points:
                m.solar_module_points ??
                JSON.stringify(
                    (m.path ?? []).map((point) => ({
                        x: point[1],
                        y: point[0],
                    })),
                ),
        }));
    }

    return [];
}

function _normalizeFieldSegments(segment, settings, originalSolarModules) {
    const fallbackName = _resolveSegmentName(segment, settings);
    const isObstacle = segment.type === 'obstacle';
    const solarModules = isObstacle
        ? []
        : _resolveSolarModules(segment, originalSolarModules);
    const sourceFieldSegments = Array.isArray(segment.field_segments)
        ? segment.field_segments
        : null;

    if (sourceFieldSegments?.length) {
        const mapped = sourceFieldSegments.map((item) => {
            const srcPoints = Array.isArray(item.field_segment_points)
                ? item.field_segment_points
                : item.polygon;

            const polygon = Array.isArray(srcPoints)
                ? srcPoints.map(_toFieldSegmentPoint).filter((pt) => pt != null)
                : [];

            const entry = {
                name: item.name ?? fallbackName,
                polygon,
            };
            if (!isObstacle) entry.solar_modules = solarModules;
            return entry;
        });

        const valid = mapped.filter((item) => item.polygon.length > 0);
        if (valid.length > 0) return valid;
    }

    const fieldSegmentPoints = _ringToFieldSegmentPoints(segment.ring);
    const entry = {
        name: fallbackName,
        polygon: fieldSegmentPoints,
    };
    if (!isObstacle) entry.solar_modules = solarModules;
    return [entry];
}

function _resolveSegmentName(segment, settings = {}) {
    const fromSettings =
        typeof settings.name === 'string' ? settings.name.trim() : '';
    if (fromSettings) return fromSettings;

    const fromSegment =
        typeof segment?.name === 'string' ? segment.name.trim() : '';
    if (fromSegment) return fromSegment;

    return segment?.type === 'obstacle' ? 'Obstaculo' : 'Segmento';
}

/**
 * Build custom_inputs for Alurack, combining:
 *  - Library-known keys (algorithm, azimuth_deg, ...)
 *  - Alurack extras coming in through `settings` (sloof_type, gclamp_type, ...)
 *  - The solution_fields group of extra engineering dimensions
 *  - Any `segmentCustomInputs` previously stored on the segment
 */
function _mergeCustomInputs(
    segmentCustomInputs,
    settings = {},
    drawingType,
    presetConfiguration = null,
) {
    const algoMap = { adaptive: 'dynamic', grid: 'grid' };
    const fillMap = { fill: 'fill', units: 'u', kW: 'kw', kw: 'kw' };

    const mappedAlgorithm =
        algoMap[settings.layoutAlgorithm] ?? settings.layoutAlgorithm;
    const mappedFillAlgorithm =
        fillMap[settings.panelFillMode] ?? settings.panelFillMode;

    const _orientationToCode =
        settings.panelOrientation === 'vertical'
            ? 'P'
            : settings.panelOrientation === 'horizontal'
              ? 'L'
              : (settings.panelOrientation ?? undefined);

    const librarySettings = {
        // ── Backend-processed fields ──────────────────────────────────────
        algorithm: mappedAlgorithm ?? 'dynamic',
        fill_algorithm: mappedFillAlgorithm ?? 'fill',
        building_height: settings.buildingHeight, // seg.height is backend-owned
        ceil_tilt: settings.roofSlopeDeg, // roof slope in Alurack terms

        // ── Library-only state (no root-level equivalent) ─────────────────
        elevation: settings.elevation,
        group_cols: settings.groupCols,
        group_rows: settings.groupRows,
        panel_depth: settings.panelDepth,
        panel_height: settings.panelHeight,
        panel_lift_height: settings.panelLiftHeight,
        // Persisted so the ReadOnly viewer can reconstruct back_to_back without
        // re-deriving it from system_uid at read time.
        panel_pairing_mode: settings.panelPairingMode || undefined,
        panel_sun_glare_intensity: settings.panelSunGlareIntensity,
        panel_watts: settings.panelWatts,
        panel_width: settings.panelWidth,
        ridge_offset_fraction: settings.ridgeOffsetFraction,
        roof_slope_azimuth_deg: settings.roofSlopeAzimuthDeg,
        selected_panel_code: settings.selectedPanelCode,
        target_kw: settings.targetKw,
        target_panel_count: settings.targetPanelCount,
    };

    const alurackRoot = {};
    for (const key of ALURACK_ROOT_CUSTOM_INPUT_KEYS) {
        if (settings[key] !== undefined && settings[key] !== null) {
            alurackRoot[key] = settings[key];
        } else if (
            segmentCustomInputs &&
            segmentCustomInputs[key] !== undefined
        ) {
            alurackRoot[key] = segmentCustomInputs[key];
        }
    }

    const solutionFields = {};
    for (const key of ALURACK_SOLUTION_FIELD_KEYS) {
        if (settings[key] !== undefined && settings[key] !== null) {
            solutionFields[key] = settings[key];
        } else if (
            segmentCustomInputs &&
            segmentCustomInputs[key] !== undefined
        ) {
            solutionFields[key] = segmentCustomInputs[key];
        }
    }

    // Mirrors the legacy additionalCustomInputs() logic in createSegment.js /
    // updateSegment.js: if a preset_configuration ID exists and the user has
    // not modified any solution_fields, forward the preset ID only so the
    // template resolves field values from the preset itself.
    // If fields were changed, drop the preset ID and expand the individual
    // values so the backend stores exactly what the user set.
    // When no presetConfiguration object is available for comparison, always
    // expand (safe default: never silently discard user changes).
    const presetId = alurackRoot.preset_configuration ?? null;
    const { hasChanges = true } =
        presetId && presetConfiguration
            ? detectFieldChanges(presetConfiguration, solutionFields)
            : { hasChanges: true };

    // Build the additional custom inputs: preset ref XOR individual fields.
    const additionalCustomInputs =
        presetId && !hasChanges
            ? { preset_configuration: presetId }
            : { ...solutionFields };

    // Exclude preset_configuration from alurackRoot — it is now managed
    // exclusively via additionalCustomInputs above so it is not duplicated.
    // presetId was already extracted above; the _ prefix marks it as unused.
    const {
        //eslint-disable-next-line no-unused-vars
        preset_configuration: _preset_configuration,
        ...alurackRootWithoutPreset
    } = alurackRoot;

    return {
        ...librarySettings,
        ...(segmentCustomInputs || {}),
        ...alurackRootWithoutPreset,
        ...additionalCustomInputs,
        // Override stale segment values with current library state.
        // tilt, panel_orientation and azimuth_deg must always reflect what the
        // 3D planner has, not whatever was last saved — they travel via
        // top-level fields too, but custom_inputs is the only path the ReadOnly
        // viewer reads when top-level is absent.
        // NOTE: azimuth_deg is the PANEL facing direction (e.g. 180 = south).
        //       It is distinct from building_heading / roof_slope_azimuth_deg
        //       which store the water-drainage direction.
        ...(settings.tiltDeg != null && { tilt: settings.tiltDeg }),
        ...(_orientationToCode != null && {
            panel_orientation: _orientationToCode,
        }),
        ...(settings.azimuthDeg != null && {
            azimuth_deg: settings.azimuthDeg,
        }),
        drawing_type: drawingType,
    };
}

/**
 * Convert a segment emitted by onSegmentCreate/onSegmentUpdate plus its
 * settings (from the library store) into an Alurack REST payload.
 *
 * @param {object} params
 * @param {object} params.segment - Library segment { id, type, ring, ... }
 * @param {object} [params.settings] - Roof settings from roofSettingsById
 * @param {object} [params.context] - { commercialOfferId, parentFieldSegmentId, defaultSolarModuleId, systemId }
 * @returns {object} Alurack API-compatible payload
 */
export function librarySegmentToAlurack({ segment, settings, context = {} }) {
    const {
        commercialOfferId,
        parentFieldSegmentId,
        defaultSolarModuleId,
        originalSegment,
        presetConfiguration,
        systemId,
    } = context;
    const alurackType = _resolveAlurackType(segment);

    const polygon = _ringToPolygon(segment.ring);
    const normalizedFieldSegments = _normalizeFieldSegments(
        segment,
        settings,
        originalSegment?.solar_modules,
    );

    const base = {
        id: segment.id,
        type: alurackType,
        polygon,
        field_segments: normalizedFieldSegments,
        commercial_offer: commercialOfferId,
        parent_field_segment: parentFieldSegmentId,
        total_area: segment.total_area ?? 0,
        segment_color: segment.segment_color ?? '#8c8c8c',
        segment_stroke_color: segment.segment_stroke_color ?? '#11EFD9',
    };

    if (segment.type === 'roof') {
        return _buildRoofPayload(base, segment, settings, {
            defaultSolarModuleId,
            drawingType: _resolveDrawingType(segment),
            originalSegment,
            presetConfiguration,
            systemId,
        });
    }
    if (segment.type === 'obstacle') {
        return _buildObstaclePayload(base, segment);
    }
    if (segment.type === 'image') {
        return _buildImagePayload(base, segment);
    }
    return base;
}

/**
 * Compute ASCE wind-zone metadata for rectangular segments.
 *
 * Mirrors the legacy handleOnClickModule / createSegment / updateSegment logic:
 *   markGroupEdges(modules_groups) → setCeilZone({ ... })
 *
 * Uses `originalGroupedCells` (4D backend structure with real GPS panel
 * coordinates) as the primary source, exactly like the legacy code does with
 * `segment.grouped_cells`. The computed `groupedCells` from the library uses
 * a default projection that may differ from the actual map center, so GPS
 * coordinates produced by xyToLngLat can be slightly off for turfPolygon
 * intersection checks.  Only when no saved cells are available (new segment
 * not yet persisted) does it fall back to the library-computed structure.
 *
 * Only applicable to POLYGON_RECTANGLE segments; returns [] otherwise.
 * Requires window.google (Google Maps SDK) to be loaded.
 *
 * @param {Array}  originalGroupedCells - 4D grouped_cells from the Redux/API
 *                                        segment (may be null for new segments)
 * @param {object} settings             - Roof settings from roofSettingsById
 * @param {Array}  polygonRing          - Segment ring as [[lat, lng]]
 * @param {string} drawingType          - POLYGON_RECTANGLE constant when applicable
 * @param {Array}  [fallbackGroupedCells] - 3D structure from buildGroupedCellsFromLayout
 * @returns {Array}
 */
function _buildAsceMeta(
    originalGroupedCells,
    settings,
    polygonRing,
    drawingType,
    fallbackGroupedCells,
) {
    if (drawingType !== POLYGON_RECTANGLE) return [];

    const google = window.google;
    if (!google?.maps?.geometry) return [];

    // Prefer backend-stored grouped_cells (real GPS coordinates, 4D structure).
    // Fall back to library-computed groupedCells wrapped into the 4D format.
    let sourceGroups;
    if (
        Array.isArray(originalGroupedCells) &&
        originalGroupedCells.length > 0
    ) {
        // Legacy path: 4D [group][cell][row][col] from backend
        sourceGroups = originalGroupedCells;
    } else if (
        Array.isArray(fallbackGroupedCells) &&
        fallbackGroupedCells.length > 0
    ) {
        // New-segment path: wrap 3D [group][row][col] into 4D
        sourceGroups = fallbackGroupedCells.map((g) => [g]);
    } else {
        return [];
    }

    try {
        const modulesGroups = getModulesGroupV2(sourceGroups);
        const markedGroupEdges = markGroupEdges(modulesGroups);

        return setCeilZone({
            buildingHeight: settings.buildingHeight ?? 3,
            ceilTilt: settings.roofSlopeDeg ?? 0,
            google,
            groups: markedGroupEdges,
            // For ASCE we need the polygon's drainage direction (building
            // heading), not the panel-facing azimuth the user may have
            // changed via right-click. building_heading is auto-computed from
            // the polygon ring by _syncRectangleHeading and round-trips
            // through custom_inputs, so it always reflects the current shape.
            heading: settings.building_heading ?? settings.azimuthDeg ?? 180,
            polygon: polygonRing,
            sloofType: settings.sloof_type ?? null,
        });
    } catch (_err) {
        return [];
    }
}

function _buildRoofPayload(base, segment, settings = {}, context = {}) {
    const orientationMap = { vertical: 'P', horizontal: 'L' };
    const resolvedName = _resolveSegmentName(segment, settings);
    const panelsLayout = Array.isArray(segment.panels_layout)
        ? segment.panels_layout
        : [];
    const hasPanelsFromLibrary = panelsLayout.length > 0;

    // grouped_cells from library layout (3D: [group][row][col]).
    // Falls back to originalSegment.grouped_cells (4D: [group][cell][row][col])
    // when the library fired onSegmentUpdate for a settings-only change and
    // didn't recompute the panel layout — we must not lose the previously
    // saved cell structure in that case.
    const libraryGroupedCells = buildGroupedCellsFromLayout(panelsLayout);
    const groupedCells =
        libraryGroupedCells.length > 0
            ? libraryGroupedCells
            : (context.originalSegment?.grouped_cells ?? []);

    const enabledPanelCount = hasPanelsFromLibrary
        ? panelsLayout.reduce(
              (acc, group) =>
                  acc + group.panels.filter((p) => p.isEnabled).length,
              0,
          )
        : (context.originalSegment?.solar_modules_number ??
          context.originalSegment?.quantity_enabled_modules ??
          0);

    const resolvedSolarModulesNumber =
        enabledPanelCount ||
        segment.solar_modules_number ||
        settings.targetPanelCount ||
        0;

    // `selectedPanelCode` is '' (empty string) when the user picks the
    // "Custom" panel. The backend rejects '' with a UUID validation error,
    // so coerce empty/falsy values down to null. Use `||` rather than `??`
    // since `??` doesn't treat '' as missing.
    //
    // For custom panels (is_custom_module=true) solar_module MUST be null.
    // The backend derives the FK from the `panel` object it creates/updates.
    // Sending a stale UUID here causes an IntegrityError when the custom
    // panel record no longer exists in panels_panelcompany (e.g. after a DB
    // reset). alurackToLibrary may still leave a UUID in segment.solar_module
    // for segments loaded before this fix, so we guard here too.
    const resolvedSolarModuleId = settings.is_custom_module
        ? null
        : settings.selectedPanelCode ||
          segment.solar_module ||
          context.defaultSolarModuleId ||
          null;
    const frameCols = settings.groupCols ?? 1;
    const frameRows = settings.groupRows ?? 1;

    // FIX: The 3D library does NOT include `custom_inputs` in the segment
    // object it emits via onSegmentUpdate/onSegmentCreate — it is an Alurack
    // concept the library knows nothing about. Passing `segment?.custom_inputs`
    // alone always evaluates to `undefined`, so the _mergeCustomInputs fallback
    // path `segmentCustomInputs[key]` never activates, causing solution-fields
    // (beam_spacing, rail_spacing, front_base_height, lfoot_supports_width,
    // support_length, etc.) stored in the previous save to be silently dropped.
    // Using `originalSegment.custom_inputs` as the base ensures every
    // previously saved field is preserved unless explicitly overridden by the
    // current library settings.
    const customInputs = _mergeCustomInputs(
        segment?.custom_inputs ?? context.originalSegment?.custom_inputs,
        settings,
        context.drawingType ?? base.type,
        context.presetConfiguration ?? null,
    );
    const convertTomm = (value) =>
        typeof value === 'number' ? value * 1000 : value;

    const isCustomModule =
        settings.is_custom_module ?? customInputs.is_custom_module ?? false;

    let newPanel = null;
    if (isCustomModule) {
        newPanel = {
            ...(segment.panel || {}),
            width:
                convertTomm(settings.panelWidth) ??
                convertTomm(segment.panel?.width),
            height:
                convertTomm(settings.panelHeight) ??
                convertTomm(segment.panel?.height),
            depth:
                convertTomm(settings.panelDepth) ??
                convertTomm(segment.panel?.depth),
            weight: customInputs.panel_weight ?? segment.panel?.weight,
            name: customInputs.panel_name ?? segment.panel?.name,
            brand:
                customInputs.brandName ??
                segment.panel?.brandName ??
                segment.panel?.brand,
            brandName:
                customInputs.brandName ??
                segment.panel?.brandName ??
                segment.panel?.brand,
            code:
                customInputs.panel_name ??
                segment.panel?.name ??
                segment.panel?.code,
            max_power:
                settings.panelWatts ??
                segment.panel?.watts ??
                segment.panel?.max_power,
        };
    }

    // ASCE wind-zone metadata — mirrors legacy createSegment/updateSegment.
    // Primary source: originalSegment.grouped_cells (4-D GPS structure from
    // the backend). Fallback: groupedCells computed from the library layout
    // (used for new segments not yet persisted to the backend).
    //
    // When both sources are empty (settings-only update, no panels recomputed),
    // fall back to the last saved asce_metadata so wind-zone tables in the
    // report template are not wiped.
    const computedAsceMeta = _buildAsceMeta(
        context.originalSegment?.grouped_cells ?? null,
        settings,
        _ringToPolygon(segment.ring),
        context.drawingType,
        libraryGroupedCells,
    );
    const asceMetadata =
        computedAsceMeta.length > 0
            ? computedAsceMeta
            : (context.originalSegment?.asce_metadata ?? []);

    // getModulesGroupV2 expects [group][cell][row][col] (4D) but
    // buildGroupedCellsFromLayout returns [group][row][col] (3D, cell=0 implicit).
    // Wrap each 3D group in an array to add the missing cell dimension when
    // using the library-computed cells. The originalSegment.grouped_cells is
    // already 4D (from restoreGroups in formatSegment) so no wrapping needed.
    const groupedCellsFor4D = hasPanelsFromLibrary
        ? libraryGroupedCells.map((g) => [g])
        : groupedCells;

    return {
        ...base,
        asce_metadata: asceMetadata,
        name: resolvedName,
        azimuth: settings.azimuthDeg ?? 180,
        col_spacing: settings.panelGapX ?? 0.02,
        custom_inputs: customInputs,
        frame_col_spacing: settings.groupGapX ?? 0.02,
        frame_cols_number: frameCols,
        frame_row_spacing: settings.groupGapY ?? 1,
        frame_rows_number: frameRows,
        grouped_cells: groupedCells,
        groups: prepareModulesGroups(getModulesGroupV2(groupedCellsFor4D)),
        // Legacy top-level fields expected by the backend and report template.
        // is_custom_module is used by the backend to decide whether to update
        // the panel FK or create/update a custom panel record.
        height: settings.buildingHeight ?? 0,
        is_custom_module: isCustomModule,
        ...(isCustomModule && newPanel ? { panel: newPanel } : {}),
        orientation: orientationMap[settings.panelOrientation] ?? 'P',
        roof_tilt: settings.roofSlopeDeg ?? 0,
        row_spacing: settings.panelGapY ?? 0.02,
        safe_zone: settings.setbackM ?? 0,
        solar_module: resolvedSolarModuleId,
        // solar_modules must be present both inside field_segments[0] (where
        // the backend creates panel_module records) AND at the top level
        // (where the backend updates the solar_characterization snapshot used
        // by the BOM template to compute material quantities).
        solar_modules: base.field_segments?.[0]?.solar_modules ?? [],
        solar_modules_number: resolvedSolarModulesNumber,
        system:
            settings.system ||
            customInputs.system ||
            context.originalSegment?.system ||
            segment.system ||
            context.systemId ||
            null,
        system_uid: settings.system_uid ?? customInputs.system_uid ?? null,
        tilt: settings.tiltDeg ?? 21,
        zIndex: segment.zIndex ?? 0,
    };
}

function _buildObstaclePayload(base, segment) {
    const isCircle = segment.isCircle === true;
    const drawingType = isCircle ? POLYGON_CIRCLE : POLYGON_OBSTACLE;
    const custom_inputs = {
        drawing_type: drawingType,
        height: segment.height ?? 0,
        ...(isCircle && segment.diameter != null
            ? { diameter: segment.diameter }
            : {}),
        ...(segment.elevation != null ? { elevation: segment.elevation } : {}),
        ...(segment.setbackM != null ? { setback_m: segment.setbackM } : {}),
        ...(segment.slopeDeg != null ? { slope_deg: segment.slopeDeg } : {}),
        ...(segment.slopeAzimuthDeg != null
            ? { slope_azimuth_deg: segment.slopeAzimuthDeg }
            : {}),
    };

    return {
        ...base,
        azimuth: 0,
        col_spacing: 0,
        custom_inputs,
        frame_col_spacing: 0,
        frame_cols_number: 0,
        frame_row_spacing: 0,
        frame_rows_number: 0,
        height: segment.height ?? 0,
        name: segment.name ?? '',
        orientation: 'P',
        row_spacing: 0,
        safe_zone: segment.setbackM ?? 0,
        solar_module: null,
        solar_modules: [],
        tilt: 0,
    };
}

function _buildImagePayload(base, segment) {
    return {
        ...base,
        azimuth: 0,
        col_spacing: 0,
        frame_col_spacing: 0,
        frame_cols_number: 1,
        frame_row_spacing: 0,
        frame_rows_number: 1,
        height: segment.height ?? 0,
        name: segment.name ?? '',
        opacity: Math.round((segment.opacity ?? 1) * 100),
        orientation: 'P',
        project_on_roofs: segment.projectOnRoofs ?? true,
        rotate: segment.rotation ?? 0,
        // imageUrl in the library store maps to `image` (S3 URL) on the backend.
        // Only include it when it is a real hosted URL — skip data URLs (local
        // previews) so we never accidentally send a multi-MB base64 blob to the
        // segments endpoint (it only accepts a URL string, not binary data).
        ...(segment.imageUrl && !segment.imageUrl.startsWith('data:')
            ? { image: segment.imageUrl }
            : {}),
        row_spacing: 0,
        safe_zone: 0,
        solar_module: null,
        tilt: 0,
        type: POLYGON_MAP,
    };
}

/**
 * Convert library panel-toggle entries to the shape Alurack needs.
 *
 * Library emits: [{ panelKey: "roofId:groupIdx:panelIdx", isEnabled: bool }]
 * Alurack needs: the panel module UUID + parentId + new enabled state.
 *
 * Since panel UUIDs come from the backend, we map by position using the
 * segment's `solar_modules` array grouped by group index.
 */
export function libraryPanelTogglesToAlurack(toggles, segmentsMap) {
    const results = [];

    for (const { panelKey, isEnabled } of toggles) {
        const parts = panelKey.split(':');
        if (parts.length !== 3) continue;

        const [roofId, groupIdxStr, panelIdxStr] = parts;
        const groupIdx = parseInt(groupIdxStr, 10);
        const panelIdx = parseInt(panelIdxStr, 10);
        const segment = segmentsMap[roofId];
        if (!segment?.solar_modules) continue;

        const modulesInGroup = segment.solar_modules.filter(
            (m) => m.group === groupIdx,
        );
        const module = modulesInGroup[panelIdx];

        if (module?.id) {
            results.push({
                moduleId: module.id,
                parentId: roofId,
                isEnabled,
            });
        }
    }

    return results;
}

/**
 * Build a simple map of segment ID → segment from the Redux segments array.
 */
export function buildSegmentsMap(segments) {
    const map = {};
    for (const seg of segments) {
        map[seg.id] = seg;
    }
    return map;
}
